import React, { useContext, useEffect, useMemo, useState } from 'react';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import { MenuProps, Row } from 'antd';
import ClientDashboardCard from 'views/Dashboard/ClientDashboardCard';
import styles from './Dashboard.module.scss';
import { DashboardViewProps } from 'enums/DashboardViewEnums';
import CreateProductModal from 'shared/components/CreateProductModal';
import EditGoalModal from 'shared/components/EditGoalModal';
import { ProductEmission } from 'models/ProductEmission/productEmission.model';
import { TotalProductEmissionAnnual } from 'models/TotalProductEmissionAnnual/totalProductEmissionAnnual.model';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import businessModelOptions from './businessModelOption';
import DashboardService, {
  EmissionParams,
} from 'services/DashboardService/dashboard.service';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import { SocketContext } from 'context/SocketContext';
import { initSocket } from 'shared/utils/initSocket';
import EmptyStateComponent from 'shared/components/EmptyStateComponent';
import { addProductSubTitle, addProductTitle } from 'constants/constants';
import ValueChainService from 'services/ValueChainService/valuechain.service';
import ProcessingFacilityModal from 'shared/components/Products/ProcessingFacilityModal';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import DemoVideoService from 'services/DemoVideoService/demoVideo.service';
import { DemoVideoTypes } from 'enums/demoVideoEnums';
import VideoModal from 'shared/components/DemoVideoModal/VideoModal';
import ProductEmissionCharts from './ProductEmissionCharts';
import {
  handleChangeBusinessModelProductEmission,
  handleChangeBusinessModelAnnualEmission,
  downloadProductEmissionCSV,
  downloadAnnualEmissionCSV,
  convertToOptionsAnnual,
  convertToOptions,
  handleSortProductEmission as handleSortProductEmissionUtil,
  handleSortAnnualEmission as handleSortAnnualEmissionUtil,
} from './DashboardUtils';
import EmissionService from 'services/ProductsService/emission.service';

export interface CategoryOptions {
  emission: number | undefined;
  name: string | undefined;
  key: string | number | undefined;
}
const defaultParams = {
  business_model: businessModelOptions[0].value,
  page: 1,
};

export interface EmissionState {
  productEmissionList?: ProductEmission[];
  productEmissonAnnualList?: TotalProductEmissionAnnual[];
  params: EmissionParams;
}

const Dashboard: React.FC = () => {
  const {
    fetchDemoWatchStatus,
    demoWatchedStatus,
    fetchDemoVideoLink,
    demoVideos,
    updateDemoWatchStatus,
  } = DemoVideoService();
  const { fetchTotalEmissionProducts, totalEmissionProducts } =
    EmissionService();

  const { fetchGoalProduct, goalProduct, setGoal, updateGoal } =
    DashboardService();

  const {
    fetchProductEmission,
    productEmissionList,
    fetchProductEmissionAnnual,
    productEmissonAnnualList,
    productEmissionLoading,
    productEmissionPagination,
    productEmissionAnnualLoading,
    productEmissionAnnualPagination,
    downloadGraph,
  } = DashboardService();

  const { fetchProcessingFacilities, processingFacilityList } =
    ValueChainService();

  const navigate = useNavigate();
  const [showModalState, setShowModalState] = useState<boolean>(false);
  const [sortedProductEmissionList, setSortedProductEmissionList] =
    useState<EmissionState>({ productEmissionList: [], params: defaultParams });
  const [sortedAnnualEmissionList, setSortedAnnualEmissionList] =
    useState<EmissionState>({
      productEmissonAnnualList: [],
      params: defaultParams,
    });
  const [createProcessingFacility, setCreateProcessingFacility] =
    useState<boolean>(false);
  const [playVideo, setPlayVideo] = useState<boolean>(false);
  const toggleVideoDisplay = () => setPlayVideo(prev => !prev);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const checkoutSessionId = params.get('checkout-session-id');
    if (token) {
      localStorage.setItem('user', token);
    }
    if (checkoutSessionId) {
      navigate(AppRoutes.DASHBOARD);
    }

    fetchProcessingFacilities();
    fetchDemoWatchStatus();
  }, []);

  // Demo video implementation
  useEffect(() => {
    if (demoWatchedStatus && !demoWatchedStatus?.pcfStatus) {
      fetchDemoVideoLink();
      toggleVideoDisplay();
    } else {
      updateDemoWatchStatus(DemoVideoTypes.PCF);
    }
  }, [demoWatchedStatus]);

  useEffect(() => {
    if (productEmissionList) {
      setSortedProductEmissionList(prev => ({
        ...prev,
        productEmissionList,
      }));
    }
    if (productEmissonAnnualList) {
      setSortedAnnualEmissionList(prev => ({
        ...prev,
        productEmissonAnnualList,
      }));
    }
  }, [productEmissionList, productEmissonAnnualList]);

  const [editModalProductState, setEditModaProductState] =
    useState<boolean>(false);
  const handleModalState = () => setShowModalState(false);
  const { organizationData } = useParentOrganizationContext();
  const handleOpenStateConfirm = () => setShowModalState(true);

  const handleEditProductModal = () => setEditModaProductState(false);

  const handleOpenProductModal = () => setEditModaProductState(true);

  /*Chart 1 calculations */
  const totalcarbonEmissionOfAllProducts = parseFloat(
    Number(totalEmissionProducts?.totalCo2e).toFixed(2)
  );

  const productCarbonFootPrintData = useMemo(
    () => convertToOptions(sortedProductEmissionList.productEmissionList || []),
    [sortedProductEmissionList]
  );

  const annualProductChartData = useMemo(
    () =>
      convertToOptionsAnnual(
        sortedAnnualEmissionList.productEmissonAnnualList || []
      ),
    [sortedAnnualEmissionList]
  );

  useEffect(() => {
    fetchTotalEmissionProducts();
    handleFetchEmission(defaultParams, true, true);
    fetchGoalProduct({ type: 'company' });
  }, []);
  const user = localStorage.getItem('userDetails');
  let parsedUser;
  if (user !== null) {
    parsedUser = JSON.parse(user);
  }

  const handleOpenCreateProductModal = () => {
    if (
      processingFacilityList?.length &&
      organizationData?.organization?.name
    ) {
      handleOpenStateConfirm();
    } else {
      setCreateProcessingFacility(true);
    }
  };
  const handleProcessingFacilityModalClose = () =>
    setCreateProcessingFacility(false);

  const handleFetchEmission = (
    params: EmissionParams,
    productsEmission?: boolean,
    annualEmission?: boolean
  ) => {
    productsEmission && fetchProductEmission(params);
    annualEmission && fetchProductEmissionAnnual(params);
  };

  const { setSocketConnection, socket } = useContext(SocketContext);

  useEffect(() => {
    const token = localStorage.getItem('user');
    socket?.disconnect();
    const socketDetails = initSocket(token);
    setSocketConnection(socketDetails);
  }, []);

  const Loader = () => (
    <div className={styles.loaderContainer}>
      <LoaderAnimation />
    </div>
  );

  const handleSortProductEmission: MenuProps['onClick'] = ({ key }) => {
    handleSortProductEmissionUtil(
      key,
      sortedProductEmissionList,
      setSortedProductEmissionList,
      handleFetchEmission
    );
  };

  const handleSortAnnualEmission: MenuProps['onClick'] = ({ key }) => {
    handleSortAnnualEmissionUtil(
      key,
      sortedAnnualEmissionList,
      setSortedAnnualEmissionList,
      handleFetchEmission
    );
  };

  const handleChangeBusinessModelProductEmissionWrapper = (
    params: EmissionParams
  ) => {
    handleChangeBusinessModelProductEmission(
      params,
      setSortedProductEmissionList,
      fetchProductEmission
    );
  };

  const handleChangeBusinessModelAnnualEmissionWrapper = (
    params: EmissionParams
  ) => {
    handleChangeBusinessModelAnnualEmission(
      params,
      setSortedAnnualEmissionList,
      fetchProductEmissionAnnual
    );
  };

  const downloadProductEmissionCSVWrapper = () =>
    downloadProductEmissionCSV(downloadGraph, sortedProductEmissionList);

  const downloadAnnualEmissionCSVWrapper = () =>
    downloadAnnualEmissionCSV(downloadGraph, sortedAnnualEmissionList);

  const demoVideoUrl = demoVideos?.find(
    item => item.type === DemoVideoTypes.PCF
  )?.url;

  return (
    <DashboardContainer>
      {goalProduct ? (
        <>
          <div className={styles.contentContainer}>
            <Row className={styles.componentHeading}>
              <h1>{DashboardViewProps.HEADING}</h1>
            </Row>
            {productEmissonAnnualList?.length === 0 ? (
              <div className={styles.emptyStateContainer}>
                <div onClick={handleOpenCreateProductModal}>
                  <EmptyStateComponent
                    title={addProductTitle}
                    subTitle={addProductSubTitle}
                  />
                </div>
              </div>
            ) : (
              <>
                <Row className={styles.cardsContainer}>
                  <EditGoalModal
                    product
                    showModalState={editModalProductState}
                    handleModalCancel={handleEditProductModal}
                    handleModalConfirm={handleEditProductModal}
                    type="company"
                    setGoal={setGoal}
                    updateGoal={updateGoal}
                    preFetchedData={goalProduct}
                  />
                  <ClientDashboardCard
                    product
                    goalValueAllProducts={
                      goalProduct?.co2e ? goalProduct?.co2e : 0
                    }
                    totalcarbonEmissionOfAllProducts={Number(
                      totalcarbonEmissionOfAllProducts
                    )}
                    handleOpenModal={handleOpenProductModal}
                  />
                </Row>
                <ProductEmissionCharts
                  productCarbonFootPrintData={productCarbonFootPrintData}
                  productEmissionLoading={productEmissionLoading}
                  handleSortProductEmission={handleSortProductEmission}
                  downloadProductEmissionCSV={downloadProductEmissionCSVWrapper}
                  sortedProductEmissionList={sortedProductEmissionList}
                  productEmissionPagination={productEmissionPagination}
                  handleChangeBusinessModelProductEmission={
                    handleChangeBusinessModelProductEmissionWrapper
                  }
                  annualProductChartData={annualProductChartData}
                  productEmissionAnnualLoading={productEmissionAnnualLoading}
                  handleSortAnnualEmission={handleSortAnnualEmission}
                  downloadAnnualEmissionCSV={downloadAnnualEmissionCSVWrapper}
                  sortedAnnualEmissionList={sortedAnnualEmissionList}
                  productEmissionAnnualPagination={
                    productEmissionAnnualPagination
                  }
                  handleChangeBusinessModelAnnualEmission={
                    handleChangeBusinessModelAnnualEmissionWrapper
                  }
                />
              </>
            )}
          </div>
          {showModalState && (
            <CreateProductModal
              handleModalCancel={handleModalState}
              handleModalConfirm={handleModalState}
              showModalState={showModalState}
              userId={Number(parsedUser?.id)}
              isCreate={true}
            />
          )}
          <ProcessingFacilityModal
            modalOpen={createProcessingFacility}
            handleClose={handleProcessingFacilityModalClose}
          />
        </>
      ) : (
        <Loader />
      )}
      {playVideo && demoVideoUrl && (
        <VideoModal
          isOpen={playVideo}
          videoUrl={demoVideoUrl}
          onClose={toggleVideoDisplay}
        />
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
